package fr.frux.shared.domain.model

/**
 * Domain model for a collective harvest event.
 * This is the business logic representation, different from the DTO (#109).
 */
data class Harvest(
    val id: Long,
    // Nul pour une récolte de lieu (ADR 0035) : voir [target].
    val treeId: Long?,
    val createdBy: Long,
    // Pseudo de l'organisateur (#206), même sémantique que Tree.createdByUsername : renseigné par
    // le backend, null si absent — jamais un id maquillé en nom.
    val createdByUsername: String? = null,
    val scheduledDate: Long, // Timestamp in milliseconds, comme Tree.createdAt
    val description: String?,
    val participants: List<Long>,
    val status: HarvestStatus,
    val createdAt: Long, // Timestamp in milliseconds
    val maxParticipants: Int? = null,
    // Détail des participants avec pseudo (#206), organisateur inclus. Vide si le backend n'a rien
    // renvoyé (contrat rétro-compatible).
    val participantsDetails: List<HarvestParticipant> = emptyList(),
    // Nombre de participants, organisateur inclus (#277) : seule donnée de participation servie
    // aux appels anonymes, où participants/participantsDetails arrivent vides. Défaut dérivé des
    // identités pour les constructions locales ; attention, copy(participants = …) ne le recalcule pas.
    val participantsCount: Int = participants.size,
    // Récolte passée (#435) : dérivée par le serveur (date prévue dépassée au-delà d'un délai),
    // jamais un statut. Le join est refusé, le leave et les gestes de l'organisateur restent permis.
    val expired: Boolean = false,
    val siteId: Long? = null,
    val siteName: String? = null,
    // Invitation du propriétaire du lieu (ADR 0035) : l'organisateur reçoit chez lui.
    val hostedByOwner: Boolean = false,
) {
    /** Lieu si [siteId] est posé, arbre sinon. Une récolte sans l'un ni l'autre est un contrat rompu. */
    val target: HarvestTarget
        get() =
            when {
                siteId != null -> HarvestTarget.Site(siteId)
                treeId != null -> HarvestTarget.Tree(treeId)
                else -> error("Récolte $id sans arbre ni lieu")
            }

    /** Capacité pleine : `null` = sans limite, jamais pleine. Organisateur inclus dans le compte. */
    val isFull: Boolean
        get() = maxParticipants != null && participantsCount >= maxParticipants
}

/**
 * Participant d'une récolte avec pseudo (#206). Domaine pur, aucune dépendance DTO (#109).
 */
data class HarvestParticipant(
    val id: Long,
    val username: String?,
)

/**
 * Statut métier d'une récolte, indépendant du DTO transport (`fr.frux.shared.dto.HarvestStatus`,
 * cf. #109) — même sémantique, recopiée côté domaine.
 */
enum class HarvestStatus {
    PLANNED,
    ONGOING,
    COMPLETED,
    CANCELLED,
    ;

    /** États terminaux : plus aucune transition ni join/leave possibles. */
    val isTerminal: Boolean
        get() = this == COMPLETED || this == CANCELLED

    /** Une récolte n'accepte de nouveaux participants que tant qu'elle est à venir ou en cours. */
    val acceptsParticipants: Boolean
        get() = this == PLANNED || this == ONGOING

    /**
     * Transitions autorisées par l'organisateur (machine à états stricte, cf. #31) — miroir de
     * `fr.frux.shared.dto.HarvestStatus.canTransitionTo` (#109 : le domaine ne dépend pas du DTO).
     */
    fun canTransitionTo(target: HarvestStatus): Boolean =
        target in
            when (this) {
                PLANNED -> setOf(ONGOING, CANCELLED)
                ONGOING -> setOf(COMPLETED, CANCELLED)
                COMPLETED, CANCELLED -> emptySet()
            }
}
